import React from 'react';
import { useRouter } from 'next/router';

import WebpImg from 'components/WebpImg';

import { ratingEmoji } from 'utils/ageProducts';

// 年齡頁共用區塊：導入文案、FAQ、最愛品牌。
// /age/[ageGroup]/[category] 與 /age/[ageGroup]/[category]/[subCategory] 共用。

// 把 **粗體** markdown 轉成 <strong>
const renderBold = (text) =>
  String(text || '')
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, idx) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <strong key={idx} className="font-bold">
          {part.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
    );

// 導入文案（heading 由各頁帶入；intro 支援 **粗體** markdown）
export function IntroSection({ heading, intro }) {
  if (!intro) return null;
  return (
    <section className="pb-12 lg:pb-16">
      <h2 className="pb-4 text-xl font-bold text-[#111928] lg:text-2xl">
        {heading}
      </h2>
      <div className="whitespace-pre-line text-base leading-8 text-sorra-gray-900">
        {renderBold(intro)}
      </div>
    </section>
  );
}

// 常見問題 FAQ：灰底卡片、有序號、+/− 展開
export function FaqSection({ heading, faq }) {
  const [openIndex, setOpenIndex] = React.useState(null);
  if (!faq || faq.length === 0) return null;
  return (
    <section className="pb-12 lg:pb-16">
      <h2 className="pb-4 text-xl font-bold text-[#111928] lg:text-2xl">
        {heading}
      </h2>
      <div className="flex flex-col gap-y-3">
        {faq.map((item, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div key={idx} className="rounded-lg bg-[#F5F5F5] px-4 py-4 lg:px-6">
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="flex w-full items-start justify-between gap-x-4 text-left"
              >
                <span className="text-base font-medium text-[#111928]">
                  {idx + 1}. {item.question}
                </span>
                <span className="shrink-0 text-2xl font-light leading-6 text-[#4A4A4A]">
                  {isOpen ? '−' : '+'}
                </span>
              </button>
              {isOpen && (
                <div className="mt-3 whitespace-pre-line text-base leading-7 text-sorra-gray-800">
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// 最愛 5 大品牌：扁平 5 卡（logo + 名稱 + 平均評分），點擊導向品牌頁
// categoryName 用於 H2（L2 帶主分類、L3 帶細分類）
export function BrandSection({ ageName, categoryName, brands }) {
  const router = useRouter();
  if (!brands || brands.length === 0) return null;
  return (
    <section className="pb-20">
      <h2 className="pb-6 text-xl font-bold text-[#111928] lg:text-2xl">
        嚴選！{ageName} 最愛 5 大 {categoryName}品牌
      </h2>
      {/* 手機：單行橫向捲動（卡片固定 149px）；桌機：5 欄 grid */}
      <div className="no-scrollbar -mx-4 flex gap-x-4 overflow-x-auto px-4 lg:mx-0 lg:grid lg:grid-cols-5 lg:gap-6 lg:overflow-visible lg:px-0">
        {brands.slice(0, 5).map((brand) => (
          <button
            key={brand.id}
            onClick={() => router.push(`/brand/${brand.id}`)}
            className="flex w-[149px] shrink-0 flex-col text-left lg:w-auto"
          >
            <div className="h-[149px] w-[149px] overflow-hidden rounded-lg border border-gray-100 bg-white lg:aspect-square lg:h-auto lg:w-full">
              {brand.logo ? (
                <WebpImg
                  src={brand.logo}
                  className="h-full w-full object-contain"
                  alt={brand.name}
                />
              ) : (
                <img
                  src="/images/placeholders/brand.png"
                  className="h-full w-full object-contain"
                  alt={brand.name}
                />
              )}
            </div>
            <div className="mt-2 line-clamp-1 text-[14px] font-medium text-[#111928]">
              {brand.name}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-1 text-[14px]">
              <span className="text-sorra-gray-500">平均評分</span>
              {brand.rating != null && (
                <span className="font-bold text-sorra-pink">
                  {ratingEmoji(brand.rating)} {brand.rating}%
                </span>
              )}
              {brand.review_count != null && (
                <span className="text-sorra-gray-400">
                  ({brand.review_count})
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
